import { useState, useEffect, useCallback, useMemo } from 'react';
// Utils
import { v4 as uuidv4 } from 'uuid';
// Types
import { ChangeEvent } from 'react';
import { EventNotificationPeriod, IEventNotification } from '@trii/types/dist/Calendar';
import { SelectChangeEvent } from '@mui/material';
import { EventNotificationFront } from '../types';
import { isNumber } from '../utils/isNumber';

export const useNotificationsInitialDef = {
  notifications: [],
  action: {
    addNotification: () => {},
    deleteNotification: (_notificationId: string) => {},
    handleTimeFieldChange: (
      _event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      _notificationId: string,
    ) => {},
    handleTimePeriodChange: (_event: SelectChangeEvent<unknown>, _notificationId: string) => {},
    handleReminderChange: (_event: ChangeEvent<HTMLInputElement>, _notificationId: string) => {},
    resetNotifications: () => {},
  },
};

export type UseNotificationsType = {
  notifications: EventNotificationFront[];
  action: {
    addNotification: () => void;
    deleteNotification: (notificationId: string) => void;
    handleTimeFieldChange: (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      notificationId: string,
    ) => void;
    handleTimePeriodChange: (event: SelectChangeEvent<unknown>, notificationId: string) => void;
    handleReminderChange: (event: ChangeEvent<HTMLInputElement>, notificationId: string) => void;
    resetNotifications: () => void;
  };
};

export function useNotifications(eventNotifications?: IEventNotification[]): UseNotificationsType {
  const [notifications, setNotifications] = useState<EventNotificationFront[]>(
    eventNotifications ? eventNotifications : [],
  );

  const addNotification = useCallback(() => {
    const newNotification: EventNotificationFront = {
      id: uuidv4(),
      timeValue: 15, // Valor inicial de 15 minutos (en lugar de 0)
      timePeriod: EventNotificationPeriod.MINUTE,
      sendToContact: false,
    };
    setNotifications((prev) => prev.concat(newNotification));
  }, []);

  const deleteNotification = useCallback((notificationId: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
  }, []);

  const handleTimeFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, notificationId: string) => {
      const numberValue = Number(event.target.value);

      if (!isNumber(numberValue)) return;

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, timeValue: numberValue }
            : notification,
        ),
      );
    },
    [],
  );

  const handleTimePeriodChange = useCallback(
    (event: SelectChangeEvent<unknown>, notificationId: string) => {
      if (!isNumber(event.target.value)) return;

      const value = event.target.value;
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, timePeriod: value }
            : notification,
        ),
      );
    },
    [],
  );

  const handleReminderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, notificationId: string) => {
      const value = event.target.checked;
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, sendToContact: value }
            : notification,
        ),
      );
    },
    [],
  );

  const resetNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  useEffect(() => {
    if (eventNotifications) {
      // Asegurar que todas las notificaciones tengan un ID válido
      const notificationsWithIds = eventNotifications.map(n => ({
        ...n,
        id: n.id || uuidv4(), // Generar UUID si el id es null/undefined
      }));
      setNotifications(notificationsWithIds);
    }
  }, [eventNotifications]);

  const action = useMemo(
    () => ({
      addNotification,
      deleteNotification,
      handleTimeFieldChange,
      handleTimePeriodChange,
      handleReminderChange,
      resetNotifications,
    }),
    [
      addNotification,
      deleteNotification,
      handleTimeFieldChange,
      handleTimePeriodChange,
      handleReminderChange,
      resetNotifications,
    ],
  );

  return {
    notifications,
    action,
  };
}
